import * as THREE from "three";
import { cheapTextures, normalMapFrom, roughFrom } from "./mapTextures.js";

/**
 * Procedural, tileable VEHICLE surfaces — the third member of the family that
 * already covers the maps (`mapTextures`), the avatars (`avatarTextures`) and
 * the guns (`weaponTextures`). Added 2026-08-01 (mission `graphics`).
 *
 * Until now EVERY vehicle in the game — the tank, the truck, the DeLorean, the
 * arena bomb-kart, the helicopter, the saucer, the sub — was built from flat
 * `MeshStandardMaterial({ color, roughness, metalness })` with no map at all.
 * A 6 m cargo box and a 0.3 m wheel caught light identically, which is exactly
 * what makes a vehicle read as a toy brick: real vehicles are legible because
 * their surfaces are NOT uniform — paint has flake and orange peel and panel
 * gaps, plate has rivets and weld beads, rubber is matte and lugged, glass is
 * smudged and mirror-smooth.
 *
 * Contract, deliberately different from `weaponMaps`: the base colour is NOT
 * baked into the albedo. Vehicle materials are RETINTED at runtime —
 * `VehicleMesh.setDriver` repaints the hull in the driver's colour and can swap
 * in a custom skin — so a baked albedo would double-tint. Every family here
 * composites its detail over WHITE and leaves `params.color` alone, so
 * `color × map` reproduces the old flat colour plus relief. One albedo per
 * family for the whole game, not one per (family, colour).
 *
 * Budget: albedo 256², normal 256², roughness 128², six families, built ONCE
 * for the tab on the first vehicle. `userData.shared` keeps `disposeGroup` off
 * them — vehicles spawn and despawn constantly.
 *
 * UVs: call sites run `worldProjectUV(geo, tile)` so texel density is in WORLD
 * units. A truck's cargo box and a kart's bumper then wear the same size
 * rivets, which per-face 0..1 UVs could never do.
 */

const ALBEDO = 256;
const ROUGH = 128;

export type VehicleSurface = "paint" | "plate" | "metal" | "tire" | "glass" | "track";

interface Family {
  albedo: HTMLCanvasElement;
  height: HTMLCanvasElement;
  map: THREE.CanvasTexture | null;
  normal: THREE.CanvasTexture | null;
  rough: THREE.CanvasTexture | null;
}

function cvs(size: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  return { c, g: c.getContext("2d", { willReadFrequently: true })! };
}

/** Per-pixel grain straight into the ImageData — a fillRect per sample costs
 *  hundreds of ms in canvas state changes (same reason as `mapTextures`). */
function speckle(g: CanvasRenderingContext2D, n: number, alpha: number, lo: number, hi: number): void {
  const img = g.getImageData(0, 0, ALBEDO, ALBEDO);
  const d = img.data;
  for (let i = 0; i < n; i++) {
    const j = (((Math.random() * ALBEDO) | 0) + ((Math.random() * ALBEDO) | 0) * ALBEDO) * 4;
    const v = lo + Math.random() * (hi - lo);
    d[j] += (v - d[j]) * alpha;
    d[j + 1] += (v - d[j + 1]) * alpha;
    d[j + 2] += (v - d[j + 2]) * alpha;
  }
  g.putImageData(img, 0, 0);
}

function scratches(g: CanvasRenderingContext2D, n: number, len: number, col: string, w = 0.8): void {
  g.lineWidth = w;
  g.strokeStyle = col;
  for (let i = 0; i < n; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const a = Math.random() * Math.PI * 2;
    const l = len * (0.3 + Math.random());
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l);
    g.stroke();
  }
}

/** `a` draws the albedo (over white), `h` draws the height field (over grey). */
type Draw = (a: CanvasRenderingContext2D, h: CanvasRenderingContext2D) => void;

/** AUTOMOTIVE PAINT: metallic flake under a clearcoat, faint orange peel, one
 *  shut-line per tile so a big panel shows a door gap instead of nothing. */
const drawPaint: Draw = (a, h) => {
  speckle(a, 26000, 0.16, 205, 255);   // flake sparkle
  speckle(a, 20000, 0.10, 150, 205);   // flake shadow side
  // orange peel: soft low-frequency blobs, barely visible but it kills the
  // perfectly-flat clearcoat that made every hull look like moulded plastic
  for (let i = 0; i < 240; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 4 + Math.random() * 12;
    const gr = h.createRadialGradient(x, y, 0, x, y, r);
    const up = Math.random() < 0.5;
    gr.addColorStop(0, up ? "rgba(255,255,255,0.20)" : "rgba(0,0,0,0.20)");
    gr.addColorStop(1, "rgba(128,128,128,0)");
    h.fillStyle = gr; h.beginPath(); h.arc(x, y, r, 0, 7); h.fill();
  }
  // shut line (panel gap): dark groove with a lit lip below
  for (const y of [188]) {
    a.fillStyle = "rgba(0,0,0,0.30)"; a.fillRect(0, y, ALBEDO, 2.5);
    a.fillStyle = "rgba(255,255,255,0.20)"; a.fillRect(0, y + 2.5, ALBEDO, 1);
    h.fillStyle = "rgba(0,0,0,0.95)"; h.fillRect(0, y, ALBEDO, 3);
    h.fillStyle = "rgba(255,255,255,0.45)"; h.fillRect(0, y + 3, ALBEDO, 1.5);
  }
  scratches(a, 26, 16, "rgba(255,255,255,0.10)", 0.6); // swirl marks in the clearcoat
};

/** PAINTED STEEL PLATE: riveted seams, weld beads, chipped edges. The heavy
 *  industrial surface — truck cargo, tank hull, ship cannon carriage. */
const drawPlate: Draw = (a, h) => {
  // panel seams on a 128 grid, with a raised weld bead along one of them
  for (const x of [0, 128]) {
    a.fillStyle = "rgba(0,0,0,0.28)"; a.fillRect(x, 0, 3, ALBEDO);
    h.fillStyle = "rgba(0,0,0,0.9)"; h.fillRect(x, 0, 3, ALBEDO);
  }
  a.fillStyle = "rgba(0,0,0,0.26)"; a.fillRect(0, 128, ALBEDO, 3);
  h.fillStyle = "rgba(0,0,0,0.9)"; h.fillRect(0, 128, ALBEDO, 3);
  // weld bead: a chain of overlapping beads along the horizontal seam
  for (let x = 0; x < ALBEDO; x += 7) {
    const bd = h.createRadialGradient(x, 133, 0, x, 133, 6);
    bd.addColorStop(0, "rgba(255,255,255,0.75)");
    bd.addColorStop(1, "rgba(128,128,128,0)");
    h.fillStyle = bd; h.beginPath(); h.arc(x, 133, 6, 0, 7); h.fill();
    a.fillStyle = "rgba(255,255,255,0.10)"; a.beginPath(); a.arc(x, 133, 4, 0, 7); a.fill();
  }
  // rivet rows down both seams
  for (const x of [8, 120, 136, 248]) {
    for (let y = 12; y < ALBEDO; y += 26) {
      const dome = h.createRadialGradient(x - 1, y - 1, 0.5, x, y, 5);
      dome.addColorStop(0, "rgba(255,255,255,0.95)");
      dome.addColorStop(0.75, "rgba(160,160,160,0.5)");
      dome.addColorStop(1, "rgba(128,128,128,0)");
      h.fillStyle = dome; h.beginPath(); h.arc(x, y, 5, 0, 7); h.fill();
      a.fillStyle = "rgba(255,255,255,0.20)"; a.beginPath(); a.arc(x - 1, y - 1, 3, 0, 7); a.fill();
      a.fillStyle = "rgba(0,0,0,0.22)"; a.beginPath(); a.arc(x + 1.4, y + 1.4, 3, 0, 7); a.fill();
    }
  }
  // chipped paint / grime flecks
  for (let i = 0; i < 300; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 0.7 + Math.random() * 2.6;
    a.fillStyle = Math.random() < 0.5 ? "rgba(58,44,34,0.30)" : "rgba(0,0,0,0.16)";
    a.beginPath(); a.arc(x, y, r, 0, 7); a.fill();
  }
  speckle(a, 16000, 0.08, 90, 160);
};

/** BARE / SCUFFED ALLOY: brushed chassis, skids, barrels, rims. */
const drawMetal: Draw = (a, h) => {
  for (let x = 0; x < ALBEDO; x += 2) {
    const v = Math.random();
    a.fillStyle = v > 0.5 ? `rgba(255,255,255,${0.06 + v * 0.10})` : `rgba(30,32,38,${0.06 + (1 - v) * 0.12})`;
    a.fillRect(x, 0, 2, ALBEDO);
    h.fillStyle = v > 0.5 ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)";
    h.fillRect(x, 0, 2, ALBEDO);
  }
  scratches(a, 90, 26, "rgba(255,255,255,0.13)");
  scratches(a, 60, 20, "rgba(0,0,0,0.16)");
  scratches(h, 70, 24, "rgba(0,0,0,0.35)", 1.2);
  speckle(a, 22000, 0.10, 60, 130); // road grime settling into the grain
};

/** TYRE RUBBER: a directional lug tread with deep grooves + fine matte grain.
 *  Tiled around the tread band by the wheel builder's UVs. */
const drawTire: Draw = (a, h) => {
  a.fillStyle = "rgba(70,70,74,1)"; a.fillRect(0, 0, ALBEDO, ALBEDO);
  h.fillStyle = "rgba(40,40,40,1)"; h.fillRect(0, 0, ALBEDO, ALBEDO); // grooves = low
  // circumferential rain channels
  for (const x of [40, 128, 216]) {
    a.fillStyle = "rgba(24,24,26,1)"; a.fillRect(x - 5, 0, 10, ALBEDO);
  }
  // lug blocks in three rows, staggered — this is what makes it read as a tyre
  for (let row = 0; row < 3; row++) {
    const cx = [18, 84, 172][row] + (row === 1 ? 0 : 0);
    const wLug = [40, 76, 76][row];
    for (let i = 0; i < 8; i++) {
      const y = i * 32 + (row % 2 ? 16 : 0);
      const skew = row === 1 ? 0 : 6;
      a.fillStyle = "rgba(126,126,132,1)";
      h.fillStyle = "rgba(225,225,225,1)";
      for (const g of [a, h]) {
        g.beginPath();
        g.moveTo(cx, y + 3);
        g.lineTo(cx + wLug, y + 3 + skew);
        g.lineTo(cx + wLug, y + 25 + skew);
        g.lineTo(cx, y + 25);
        g.closePath();
        g.fill();
      }
      // sipes: fine slits across each lug
      a.strokeStyle = "rgba(30,30,32,0.85)"; a.lineWidth = 2;
      h.strokeStyle = "rgba(70,70,70,1)"; h.lineWidth = 2;
      for (let s = 1; s <= 2; s++) {
        const yy = y + 3 + (s * 22) / 3;
        for (const g of [a, h]) { g.beginPath(); g.moveTo(cx, yy); g.lineTo(cx + wLug, yy + skew); g.stroke(); }
      }
    }
  }
  speckle(a, 30000, 0.12, 40, 110);
};

/** GLASS: mostly clear, with wiper-arc smudge and a rain-fleck dusting so a
 *  windshield stops being a flat cyan slab. */
const drawGlass: Draw = (a, h) => {
  const sheen = a.createLinearGradient(0, 0, ALBEDO, ALBEDO);
  sheen.addColorStop(0, "rgba(255,255,255,0.30)");
  sheen.addColorStop(0.45, "rgba(190,215,235,0.05)");
  sheen.addColorStop(0.75, "rgba(255,255,255,0.22)");
  sheen.addColorStop(1, "rgba(120,150,180,0.10)");
  a.fillStyle = sheen; a.fillRect(0, 0, ALBEDO, ALBEDO);
  // wiper arcs
  a.lineWidth = 6; h.lineWidth = 6;
  for (let r = 60; r < 220; r += 14) {
    a.strokeStyle = "rgba(255,255,255,0.05)";
    a.beginPath(); a.arc(128, 300, r, Math.PI * 1.18, Math.PI * 1.82); a.stroke();
    h.strokeStyle = "rgba(255,255,255,0.06)";
    h.beginPath(); h.arc(128, 300, r, Math.PI * 1.18, Math.PI * 1.82); h.stroke();
  }
  for (let i = 0; i < 420; i++) { // rain flecks
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 0.6 + Math.random() * 1.8;
    a.fillStyle = "rgba(255,255,255,0.10)"; a.beginPath(); a.arc(x, y, r, 0, 7); a.fill();
    h.fillStyle = "rgba(255,255,255,0.25)"; h.beginPath(); h.arc(x, y, r, 0, 7); h.fill();
  }
};

/** TANK TRACK: transverse links with a guide horn and end connectors. Deep
 *  relief on purpose — this is the surface read from 10 m away. */
const drawTrack: Draw = (a, h) => {
  a.fillStyle = "rgba(120,120,126,1)"; a.fillRect(0, 0, ALBEDO, ALBEDO);
  h.fillStyle = "rgba(120,120,120,1)"; h.fillRect(0, 0, ALBEDO, ALBEDO);
  for (let i = 0; i < 8; i++) {
    const y = i * 32;
    a.fillStyle = "rgba(20,21,25,1)"; a.fillRect(0, y, ALBEDO, 7);       // link gap
    h.fillStyle = "rgba(10,10,10,1)"; h.fillRect(0, y, ALBEDO, 7);
    a.fillStyle = "rgba(168,170,178,1)"; a.fillRect(0, y + 7, ALBEDO, 5); // lit link lip
    h.fillStyle = "rgba(240,240,240,1)"; h.fillRect(0, y + 7, ALBEDO, 5);
    // guide horn down the centre of every link
    a.fillStyle = "rgba(150,152,160,1)"; a.fillRect(104, y + 12, 48, 16);
    h.fillStyle = "rgba(215,215,215,1)"; h.fillRect(104, y + 12, 48, 16);
    // end connectors
    for (const x of [16, 224]) {
      a.fillStyle = "rgba(90,92,98,1)"; a.fillRect(x, y + 13, 18, 14);
      h.fillStyle = "rgba(190,190,190,1)"; h.fillRect(x, y + 13, 18, 14);
    }
  }
  speckle(a, 26000, 0.14, 45, 120); // mud packed into the links
};

const SPECS: Record<VehicleSurface, { draw: Draw; bump: number; roughLo: number; roughHi: number }> = {
  paint: { draw: drawPaint, bump: 0.9, roughLo: 0.14, roughHi: 0.42 },
  plate: { draw: drawPlate, bump: 2.6, roughLo: 0.42, roughHi: 0.88 },
  metal: { draw: drawMetal, bump: 1.5, roughLo: 0.24, roughHi: 0.7 },
  tire: { draw: drawTire, bump: 3.4, roughLo: 0.74, roughHi: 0.99 },
  glass: { draw: drawGlass, bump: 0.5, roughLo: 0.04, roughHi: 0.22 },
  track: { draw: drawTrack, bump: 3.6, roughLo: 0.5, roughHi: 0.92 },
};

let families: Record<VehicleSurface, Family> | null = null;

function mk(canvas: HTMLCanvasElement, srgb: boolean): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.userData.shared = true; // module-cached: never disposed with a vehicle
  return t;
}

function all(): Record<VehicleSurface, Family> {
  if (families) return families;
  const t0 = performance.now();
  const cheap = cheapTextures();
  const out = {} as Record<VehicleSurface, Family>;
  for (const k of Object.keys(SPECS) as VehicleSurface[]) {
    const spec = SPECS[k];
    const { c: ac, g: ag } = cvs(ALBEDO);
    const { c: hc, g: hg } = cvs(ALBEDO);
    ag.fillStyle = "#ffffff"; ag.fillRect(0, 0, ALBEDO, ALBEDO); // detail over WHITE: colour still tints
    hg.fillStyle = "#808080"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
    spec.draw(ag, hg);
    out[k] = {
      albedo: ac, height: hc,
      map: mk(ac, true),
      normal: cheap ? null : mk(normalMapFrom(hc, spec.bump, ALBEDO), false),
      rough: cheap ? null : mk(roughFrom(hc, spec.roughLo, spec.roughHi, ROUGH), false),
    };
  }
  families = out;
  console.log(`[vehtex] generated ${Object.keys(out).length} vehicle families in ${(performance.now() - t0).toFixed(0)} ms`);
  return families;
}

/** GPU-upload every vehicle family texture NOW (under the load screen), given
 *  the renderer's warm callback. Without this a ride/kart spawning mid-match
 *  pays the glTexImage2D upload of its map/normal/rough on first draw — a hitch
 *  at the worst time. Idempotent (cached singletons → cheap no-op after the
 *  first map load). Cycle-free: the callback comes from the renderer, so this
 *  leaf module never imports it. */
export function warmVehicleTextures(warm: (t: THREE.Texture) => void): void {
  const fams = all();
  for (const k of Object.keys(fams) as VehicleSurface[]) {
    const f = fams[k];
    if (f.map) warm(f.map);
    if (f.normal) warm(f.normal);
    if (f.rough) warm(f.rough);
  }
}

/** Shared normalScale vectors — never allocated per material. */
const normalScales = new Map<string, THREE.Vector2>();

/**
 * Fold a vehicle surface's albedo/normal/roughness into MeshStandardMaterial
 * params. Unlike `weaponMaps` this does NOT set `params.color`: the caller's
 * colour survives and keeps tinting the hull, which is what lets
 * `VehicleMesh.setDriver` repaint a ride in the driver's neon.
 */
export function vehicleMaps(
  kind: VehicleSurface,
  params: THREE.MeshStandardMaterialParameters,
  strength = 1,
): THREE.MeshStandardMaterialParameters {
  const fam = all()[kind];
  params.map = fam.map ?? undefined;
  if (fam.normal) {
    params.normalMap = fam.normal;
    const sk = `${kind}|${strength}`;
    let s = normalScales.get(sk);
    if (!s) { s = new THREE.Vector2(strength, strength); normalScales.set(sk, s); }
    params.normalScale = s;
  }
  if (fam.rough) params.roughnessMap = fam.rough;
  // REFLECTIONS (2026-10-21 `graphics`): metal/glass lean harder on the scene
  // env probe so a car reads as painted steel with real sky in it, not a flat
  // tint. Free — the PMREM probe is already built/uploaded and `scene.environment`
  // drives every standard material; this only scales the specular it already
  // gets. No-op on low-spec (the probe is skipped there, so there is no env to
  // scale) → costs nothing on weak hardware. Rubber (tire/track) stays matte.
  // `??=` so an explicit caller value always wins.
  const reflect = VEHICLE_REFLECT[kind];
  if (reflect !== undefined) params.envMapIntensity ??= reflect;
  return params;
}

/** Per-surface env-reflection strength (see vehicleMaps). Only the metallic and
 *  glazed surfaces get a boost; rubber is intentionally absent (stays matte). */
const VEHICLE_REFLECT: Partial<Record<VehicleSurface, number>> = {
  glass: 2.2, metal: 1.6, plate: 1.55, paint: 1.4,
};

/** The shared albedo of a family — for call sites that must keep a reference
 *  (e.g. `VehicleMesh.defaultBodyMap`, restored when a driver leaves). */
export function vehicleAlbedo(kind: VehicleSurface): THREE.Texture | null {
  return all()[kind].map;
}

/** Build every family now. Called from the map-load warm-up so the first
 *  vehicle spawn of a match never pays a ~40 ms canvas build mid-fight. */
export function prewarmVehicleTextures(): void { all(); }
